import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Badge from "@/components/base/Badge";
import ShareBar from "@/components/feature/ShareBar";
import WorkCard from "@/components/feature/WorkCard";
import { EmptyState, Spinner } from "@/components/base/StateViews";
import { getRelatedWorks, getReviewsByWork, getWorkById } from "@/utils/data";
import { useFavorites } from "@/context/FavoritesContext";
import { useSeo } from "@/hooks/useSeo";

export default function WorkDetail() {
  const { id = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState("");
  const work = getWorkById(id);
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [id]);

  useSeo({
    title: work
      ? `${work.title} - ${work.type === "movie" ? "电影详情" : "电视剧详情"} - 亚洲天堂`
      : "作品未找到 - 亚洲天堂",
    description: work
      ? `${work.title}（${work.year}，${work.region}）${work.genres.join("、")}作品，导演${work.director}。${work.synopsis}`
      : undefined,
    keywords: work ? [work.title, ...work.genres, work.region, "影视详情", "亚洲天堂"].join(",") : undefined,
  });

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <Spinner label="正在加载作品信息…" />
      </div>
    );
  }

  if (!work) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <EmptyState
          title="作品未找到"
          description="该作品可能不存在或链接有误，去分类页看看吧。"
          icon="ri-film-line"
        />
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/category/movie" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap">
            浏览电影
          </Link>
          <Link to="/category/tv" className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap">
            浏览电视剧
          </Link>
        </div>
      </div>
    );
  }

  const favored = isFavorite(work.id);
  const related = getRelatedWorks(work);
  const workReviews = getReviewsByWork(work.id);

  const infoRows = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "类型", value: work.genres.join(" / ") },
    { label: "地区", value: work.region },
    { label: "年份", value: String(work.year) },
    { label: "上线日期", value: work.releaseDate },
    { label: "片长", value: work.duration },
  ];

  if (work.type === "tv") {
    infoRows.push({ label: "季数", value: `${work.seasons ?? 1} 季` });
    infoRows.push({
      label: "更新状态",
      value: work.airStatus === "连载" ? `连载中 · 更新至第 ${work.latestEpisode} 集` : `已完结 · 全 ${work.latestEpisode} 集`,
    });
  }

  const handleFavorite = () => {
    const next = toggle(work.id);
    setFlash(next ? "已加入收藏（保存在本地浏览器）" : "已取消收藏");
    window.setTimeout(() => setFlash(""), 2200);
  };

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative h-[300px] w-full overflow-hidden bg-primary-950 md:h-[380px]">
        <img
          src={work.still}
          alt={`${work.title} 剧照`}
          title={`${work.title} - 影视作品 - 亚洲天堂`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30"></div>
        <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-site px-4 pb-6 md:px-6 md:pb-8">
          <div className="mb-3 hidden md:block">
            <Breadcrumb items={[{ label: work.type === "movie" ? "电影" : "电视剧", to: work.type === "movie" ? "/category/movie" : "/category/tv" }, { label: work.title }]} />
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-site px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Poster */}
          <div className="lg:col-span-1">
            <div className="-mt-24 w-40 overflow-hidden rounded-lg border-4 border-background-50 bg-background-200 md:-mt-32 md:w-52">
              <img
                src={work.poster}
                alt={`${work.title} 海报`}
                title={`${work.title} - 海报 - 亚洲天堂`}
                className="h-auto w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  favored
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300 hover:text-accent-600"
                }`}
              >
                <i className={favored ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true"></i>
                {favored ? "已收藏" : "收藏作品"}
              </button>
              {flash ? <span className="text-center text-xs text-accent-600">{flash}</span> : null}
            </div>
          </div>

          {/* Main info */}
          <div className="lg:col-span-3">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">{work.title}</h1>
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-sm font-bold text-background-50">
                <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                {work.rating.toFixed(1)}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Badge tone="primary">{work.type === "movie" ? "电影" : "电视剧"}</Badge>
              {work.genres.map((g) => (
                <Badge key={g} tone="secondary">{g}</Badge>
              ))}
              {work.type === "tv" && work.airStatus ? (
                <Badge tone={work.airStatus === "连载" ? "accent" : "outline"}>{work.airStatus}</Badge>
              ) : null}
            </div>

            <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <dl className="divide-y divide-background-200">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4">
                    <dt className="w-20 shrink-0 text-sm font-medium text-foreground-500">{row.label}</dt>
                    <dd className="text-sm text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6">
              <h2 className="mb-3 font-heading text-lg font-bold text-foreground-900">剧情简介</h2>
              <p className="text-base leading-loose text-foreground-700">{work.synopsis}</p>
            </div>

            <div className="mt-6 border-t border-background-200 pt-5">
              <ShareBar title={work.title} />
            </div>
          </div>
        </div>

        {/* 影评摘要 */}
        <section className="mt-12">
          <div className="mb-4 flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl font-bold text-foreground-900">相关影评</h2>
            <Link to="/category/review" className="flex items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-accent-600 whitespace-nowrap">
              更多影评
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          {workReviews.length === 0 ? (
            <EmptyState
              title="暂无相关影评"
              description="我们还没有为该作品收录影评，欢迎浏览其他影评专题。"
              actionLabel="浏览影评专题"
              icon="ri-chat-quote-line"
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {workReviews.map((review) => (
                <Link
                  key={review.id}
                  to={`/detail/review/${review.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-accent-200"
                >
                  <h3 className="mb-2 line-clamp-2 text-base font-semibold text-foreground-900 transition-colors group-hover:text-accent-600">
                    {review.title}
                  </h3>
                  <p className="line-clamp-2 text-sm leading-relaxed text-foreground-500">{review.excerpt}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
                    <span>{review.author}</span>
                    <span>{review.date}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-thumb-up-line" aria-hidden="true"></i>
                      {review.likes}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* 相关推荐 */}
        <section className="mt-12 pb-4">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-900">相关推荐</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((rel) => (
              <WorkCard key={rel.id} work={rel} showFavorite />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}